import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";

import WizardFooter from "../wizard-footer";
import PageContent from "../page-content";
import { getProductType } from "../../../product";
import { requireLogin } from "../../../security";
import { productQueryOptions } from "../../../data/queries/products";
import { useSaveProductMutation } from "../../../data/queries/products";
import { useUser } from "../../../data/context/auth-context";
import { Button, Spinner } from "../../../ui";
import { trackSettingsComplete } from "../../../analytics";

const productLabels: Record<string, { title: string; description: string }> = {
  "photo-book": {
    title: "Couverture de votre livre photo",
    description: "Définissez le style, la couleur, le texte et la photo de couverture.",
  },
  calendar: {
    title: "Paramètres de votre calendrier",
    description: "Choisissez l'année, le thème et la couleur de votre calendrier.",
  },
};

function Settings() {
  const { uid } = useParams();
  const user = useUser();
  const { data: product, isLoading } = useQuery(productQueryOptions(uid || ""));
  const saveMutation = useSaveProductMutation();

  const setOptions = (newOptions: Record<string, unknown>) => {
    if (!product || !user) return;

    saveMutation.mutate({
      product: {
        ...product,
        options: {
          ...product.options,
          ...newOptions,
        },
      },
      userId: user.uid,
    });
  };

  if (isLoading || !product) {
    return <Spinner />;
  }

  const productType = getProductType(product.name);
  if (!productType) {
    return <Spinner />;
  }

  // Cast to any since product.options is Record<string, any> from API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ProductSettings = productType.settings as React.ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isReady = productType.isReady(product.options as any);
  const labels = productLabels[product.name] || {
    title: "Paramètres de votre produit",
    description: "Définissez les options de votre produit.",
  };

  return (
    <>
      <PageContent title={labels.title} description={labels.description}>
        <ProductSettings
          productId={product.uid}
          userId={user?.uid || ""}
          options={product.options}
          setOptions={setOptions}
        />
      </PageContent>
      <WizardFooter uid={uid} step={2} enabledSteps={isReady ? [1, 2, 3, 4] : [1, 2, 3]}>
        <Link to={`/customize/${uid}`} onClick={() => trackSettingsComplete(product.name)}>
          <Button isPrimary>Continuer</Button>
        </Link>
      </WizardFooter>
    </>
  );
}

export default requireLogin()(Settings);
